'use client';

import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import useShoppingCart from '@/hooks/useShoppingCart';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import Icon from '@/components/shared/Icons';
import Button from '@/components/shared/button/Button';
import { getShoppingCartFact } from '@/analytics/helpers/cventAnalytics/getShoppingCartFact';
import type { ShoppingCartFact } from '@/analytics/models/cventAnalytics/ShoppingCartFact';
import { queryStringConstants } from '@/constants/queryConstants';
import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';

const VenueCardDisplayType = {
  CSN_AVAILABLE: 'CSN_AVAILABLE',
  CSN_NON_AVAILABLE: 'CSN_NON_AVAILABLE',
  WHITE_LABEL: 'WHITE_LABEL',
  BUSINESS_TRANSIENT: 'BUSINESS_TRANSIENT'
} as const;

type VenueCardDisplayType = (typeof VenueCardDisplayType)[keyof typeof VenueCardDisplayType];

export type ClaimPromotionButtonProps = {
  item: ShoppingCartItem;
  strings: {
    promotionClaimed: string;
    claimPromotion: string;
    notAvailable: string;
  };
  isRFPFullAccess?: boolean;
  displayTypes?: VenueCardDisplayType[];
  isLoggedIn?: boolean;
  testID?: string;
  activeFilters?: string;
  isVpp?: boolean;
};

// Claim Promotion Button using which planner can add venues to cart with promotionId
export default function ClaimPromotionButton({
  item,
  strings,
  isRFPFullAccess,
  displayTypes,
  isLoggedIn,
  activeFilters,
  testID = 'venue',
  isVpp
}: ClaimPromotionButtonProps): JSX.Element {
  const cart = useShoppingCart();
  const isPromotionClaimed = cart?.contains(item.venueId) && cart.items[item.venueId].promotionId === item.promotionId;
  let buttonText = isPromotionClaimed ? strings.promotionClaimed : strings.claimPromotion;
  let isDisabled = false;
  const analytics = useAnalytics();
  const { mapExpanded } = useMap();
  const searchParams = useSearchParams();
  const rfpId = searchParams?.get('rfpId') ?? 'EXTERNAL_SHOPPING_CART';

  const triggerShoppingCartFact = useCallback(() => {
    const shoppingCartFact: ShoppingCartFact = getShoppingCartFact(
      'add',
      item,
      cart,
      searchParams?.get(queryStringConstants.PAGE_NUMBER),
      searchParams?.get(queryStringConstants.SORT),
      mapExpanded,
      activeFilters
    );
    analytics.reportFact<ShoppingCartFact>(shoppingCartFact);
  }, [cart, item, mapExpanded, searchParams, analytics, activeFilters]);

  const onClick = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      triggerShoppingCartFact();
      cart.updateCart({ type: 'add', item, rfpId });
    },
    [cart, item, rfpId, triggerShoppingCartFact]
  );

  if (!cart?.isRfpSentToVenue(item.venueId)) {
    // Disable button if venue is already added to rfp or rfp is sent
    isDisabled = true;
  } else if (cart?.isFull() && !cart?.contains(item.venueId)) {
    // Disable button if cart is already full for guest/logged in user w
    isDisabled = true;
  } else if (isLoggedIn && !isRFPFullAccess) {
    // Disable button if user is logged in but do not have access to manage rfp
    isDisabled = true;
  }

  // Disable button and override button text if venue is not interested in receiving rfp
  if (!!displayTypes && displayTypes.includes(VenueCardDisplayType.CSN_NON_AVAILABLE)) {
    buttonText = strings.notAvailable;
    isDisabled = true;
  }

  return (
    <div className={`${isVpp ? '' : 'mb-3'} flex flex-col`}>
      <Button
        testID={`${testID}-claim-promotion`}
        appearance={isPromotionClaimed ? 'filled' : 'lined'}
        text={buttonText}
        aria-label={buttonText}
        disabled={isDisabled}
        active={isPromotionClaimed}
        icon={isPromotionClaimed ? <Icon id="check" className="h-4 w-4 fill-[#FFFFFF]" /> : undefined}
        onClick={onClick}
        className="group rounded-xl p-2 px-3 py-1.5 text-b-md/tight"
      />
    </div>
  );
}
